import {
    createContext,
    useContext,
    useEffect,
    useId,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";
import styles from "./Picker.module.scss";

export type PickerAnnouncementPoliteness = "polite" | "assertive";

interface PickerAnnouncementState {
    id: string;
    message: ReactNode;
    politeness: PickerAnnouncementPoliteness;
}

const PickerAnnouncementContext = createContext<Dispatch<
    SetStateAction<PickerAnnouncementState | null>
> | null>(null);

/**
 * Owns a surface's screen-reader announcement: renders `children` plus a
 * visually hidden live region beside them, outside the listbox (which may only
 * own options and groups). Both regions stay mounted so a new message reads
 * as a change.
 */
export const PickerAnnouncer = ({ children }: { children: ReactNode }) => {
    const [announcement, setAnnouncement] =
        useState<PickerAnnouncementState | null>(null);

    return (
        <>
            <PickerAnnouncementContext.Provider value={setAnnouncement}>
                {children}
            </PickerAnnouncementContext.Provider>
            <div className={styles.visuallyHidden}>
                <div role="status">
                    {announcement?.politeness === "polite" &&
                        announcement.message}
                </div>
                <div role="alert">
                    {announcement?.politeness === "assertive" &&
                        announcement.message}
                </div>
            </div>
        </>
    );
};

/**
 * Announces `message` through the enclosing surface's live region while the
 * caller is mounted; the last one mounted wins. A no-op outside a surface.
 */
export const usePickerAnnouncement = (
    message: ReactNode,
    politeness: PickerAnnouncementPoliteness,
) => {
    const setAnnouncement = useContext(PickerAnnouncementContext);
    const id = useId();

    useEffect(() => {
        if (setAnnouncement === null) {
            return;
        }
        setAnnouncement({ id, message, politeness });
        return () =>
            setAnnouncement((current) => (current?.id === id ? null : current));
    }, [setAnnouncement, id, message, politeness]);
};
